import React, { useRef, useState, useCallback, useEffect } from 'react';
import Webcam from 'react-webcam';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { getCroppedImg } from './utils/cropImage';

const WebcamCapture = ({ onCapture, label, fieldName, photoType = 'passeport' }) => {
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [imgSrc, setImgSrc] = useState(null);
  const [showWebcam, setShowWebcam] = useState(false);
  const [facingMode, setFacingMode] = useState('user'); // 'user' = avant, 'environment' = arrière
  const [sourceType, setSourceType] = useState(null); // 'webcam' ou 'gallery'
  const [showCrop, setShowCrop] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  
  // Gérer les erreurs de lecture vidéo (AbortError)
  useEffect(() => {
    const handleVideoError = (event) => {
      // Ignorer les erreurs AbortError (interruption normale de la lecture)
      if (event.error && event.error.name === 'AbortError') {
        console.log('Webcam play() interrompu - normal lors de la fermeture');
        return;
      }
      // Pour les autres erreurs, les logger
      if (event.error) {
        console.warn('Erreur webcam:', event.error);
      }
    };

    // Ajouter un gestionnaire d'événements global pour les erreurs vidéo
    window.addEventListener('error', handleVideoError);
    
    return () => {
      window.removeEventListener('error', handleVideoError);
    };
  }, []);

  // Fonction utilitaire pour convertir une data URL en File (sans fetch pour éviter les problèmes CSP)
  const dataURLtoFile = useCallback((dataURL, filename) => {
    try {
      const arr = dataURL.split(',');
      const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    } catch (error) {
      console.error('Erreur lors de la conversion dataURL:', error);
      return null;
    }
  }, []);

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      setImgSrc(imageSrc);
      setShowWebcam(false);
      setSourceType('webcam');
      setShowCrop(false);
      
      // Automatiquement envoyer la photo au parent sans nécessiter de clic supplémentaire
      const file = dataURLtoFile(imageSrc, `${fieldName}.jpg`);
      if (file) {
        onCapture(file, fieldName);
        console.log('📸 Photo capturée et envoyée automatiquement');
      } else {
        console.error('Erreur lors de la conversion de la photo');
      }
    }
  }, [webcamRef, fieldName, onCapture, dataURLtoFile]);

  const retake = () => {
    const previousSource = sourceType;
    setImgSrc(null);
    setSourceType(null);
    setShowWebcam(false);
    setShowCrop(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    // Attendre un peu avant de réactiver la webcam pour éviter les conflits
    if (previousSource === 'webcam') {
      setTimeout(() => {
        setShowWebcam(true);
        setSourceType('webcam');
      }, 100);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropComplete = useCallback(async () => {
    if (!imgSrc || !croppedAreaPixels) {
      return;
    }

    try {
      const croppedImageUrl = await getCroppedImg(imgSrc, croppedAreaPixels);
      
      // Convertir l'image rognée en File (sans fetch pour éviter CSP)
      const file = dataURLtoFile(croppedImageUrl, `${fieldName}.jpg`);
      
      if (!file) {
        throw new Error('Échec de la conversion de l\'image');
      }
      
      // Mettre à jour l'image affichée avec la version rognée
      setImgSrc(croppedImageUrl);
      
      setShowCrop(false);
      
      // Passer le fichier rogné au callback
      onCapture(file, fieldName);
      console.log('✂️ Photo rognée et envoyée');
    } catch (error) {
      console.error('Erreur lors du rognage:', error);
      alert('Erreur lors du rognage de l\'image');
    }
  }, [imgSrc, croppedAreaPixels, fieldName, onCapture, dataURLtoFile]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier que c'est une image
      if (!file.type.startsWith('image/')) {
        alert('Veuillez sélectionner un fichier image');
        return;
      }

      // Lire le fichier et l'afficher en aperçu
      const reader = new FileReader();
      reader.onload = (event) => {
        const imageSrc = event.target.result;
        setImgSrc(imageSrc);
        setSourceType('gallery');
        setShowCrop(false);
        
        // Automatiquement envoyer la photo au parent
        const renamedFile = new File([file], `${fieldName}.jpg`, { type: file.type });
        onCapture(renamedFile, fieldName);
        console.log('🖼️ Photo de la galerie envoyée automatiquement');
      };
      reader.readAsDataURL(file);
    }
    
    // Réinitialiser l'input pour permettre de sélectionner le même fichier à nouveau
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleChooseFromGallery = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const handleCloseWebcam = () => {
    // Arrêter proprement la webcam avant de fermer
    if (webcamRef.current && webcamRef.current.video) {
      const video = webcamRef.current.video;
      if (video.srcObject) {
        const stream = video.srcObject;
        const tracks = stream.getTracks();
        tracks.forEach(track => track.stop());
        video.srcObject = null;
      }
    }
    setShowWebcam(false);
    setSourceType(null);
  };

  // Dimensions adaptées selon le type de photo
  const getVideoConstraints = () => {
    if (photoType === 'signature') {
      // Format paysage pour signature : 400x200 (ratio 2:1)
      return {
        width: 400,
        height: 200,
        facingMode: facingMode
      };
    } else {
      // Format portrait pour photo passeport : 320x410 (ratio ~0.78, proche du standard 35x45mm)
      return {
        width: 320,
        height: 410,
        facingMode: facingMode
      };
    }
  };

  const videoConstraints = getVideoConstraints();

  // Fonction pour changer de caméra
  const switchCamera = () => {
    // Arrêter la caméra actuelle
    if (webcamRef.current && webcamRef.current.video) {
      const video = webcamRef.current.video;
      if (video.srcObject) {
        const stream = video.srcObject;
        const tracks = stream.getTracks();
        tracks.forEach(track => track.stop());
        video.srcObject = null;
      }
    }
    
    // Fermer temporairement la webcam
    setShowWebcam(false);
    
    // Changer de caméra et réactiver après un court délai
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    
    // Réactiver la webcam avec la nouvelle caméra après un court délai
    setTimeout(() => {
      setShowWebcam(true);
    }, 200);
  };

  // Styles pour l'affichage selon le type
  const getWebcamStyle = () => {
    if (photoType === 'signature') {
      return {
        width: '400px',
        height: '200px',
        objectFit: 'cover',
        border: '2px solid #ddd',
        borderRadius: '4px'
      };
    } else {
      return {
        width: '320px',
        height: '410px',
        objectFit: 'cover',
        border: '2px solid #ddd',
        borderRadius: '4px'
      };
    }
  };

  const getImageStyle = () => {
    if (photoType === 'signature') {
      return {
        maxWidth: '400px',
        maxHeight: '200px',
        marginTop: '10px',
        border: '2px solid #ddd',
        borderRadius: '4px',
        objectFit: 'contain'
      };
    } else {
      return {
        maxWidth: '320px',
        maxHeight: '410px',
        marginTop: '10px',
        border: '2px solid #ddd',
        borderRadius: '4px',
        objectFit: 'contain'
      };
    }
  };

  // Ratio d'aspect selon le type de photo
  const getAspectRatio = () => {
    if (photoType === 'signature') {
      return 2; // 2:1 pour signature
    } else {
      return 320 / 410; // Ratio portrait pour passeport
    }
  };

  return (
    <div className="webcam-container">
      <label>{label}</label>
      
      {/* Input file caché pour la sélection depuis la galerie */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      
      {!imgSrc && !showWebcam && (
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
          <button 
            type="button" 
            className="btn btn-primary" 
            onClick={() => {
              setShowWebcam(true);
              setSourceType('webcam');
            }}
          >
            📷 Prendre une photo
          </button>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={handleChooseFromGallery}
          >
            🖼️ Choisir depuis la galerie
          </button>
        </div>
      )}

      {showWebcam && (
        <div style={{ marginTop: '10px' }}>
          <div style={{ display: 'inline-block' }}>
            <Webcam
              key={facingMode} // Force le re-render quand on change de caméra
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              style={getWebcamStyle()}
              onUserMedia={(stream) => {
                // Gérer l'erreur AbortError silencieusement
                if (stream) {
                  stream.getTracks().forEach(track => {
                    track.onended = () => {
                      // Ignorer les erreurs de fin de track
                    };
                  });
                }
              }}
              onUserMediaError={(error) => {
                // Logger seulement les vraies erreurs (pas les AbortError)
                if (error.name !== 'AbortError' && error.name !== 'NotAllowedError') {
                  console.warn('Erreur webcam:', error);
                }
              }}
            />
          </div>
          <div className="webcam-buttons" style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-success" onClick={capture}>
              Capturer
            </button>
            <button 
              type="button" 
              className="btn btn-info" 
              onClick={switchCamera}
              title={facingMode === 'user' ? 'Passer à la caméra arrière' : 'Passer à la caméra avant'}
            >
              {facingMode === 'user' ? '📷 Caméra arrière' : '📱 Caméra avant'}
            </button>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={handleCloseWebcam}
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {imgSrc && !showCrop && (
        <div style={{ marginTop: '10px' }}>
          <img src={imgSrc} alt="Captured" style={getImageStyle()} />
          <div className="webcam-buttons" style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary" onClick={() => setShowCrop(true)}>
              ✂️ Rogner la photo
            </button>
            <button type="button" className="btn btn-secondary" onClick={retake}>
              Reprendre
            </button>
          </div>
        </div>
      )}

      {imgSrc && showCrop && Cropper && (
        <div style={{ marginTop: '10px' }}>
          <div style={{ position: 'relative', width: '100%', height: '400px', background: '#000' }}>
            <Cropper
              image={imgSrc}
              crop={crop}
              zoom={zoom}
              aspect={getAspectRatio()}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              style={{
                containerStyle: {
                  width: '100%',
                  height: '100%',
                  position: 'relative'
                }
              }}
            />
          </div>
          <div style={{ marginTop: '10px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>Zoom: {Math.round(zoom * 100)}%</label>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              style={{ width: '100%', maxWidth: '400px' }}
            />
          </div>
          <div className="webcam-buttons" style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-success" onClick={handleCropComplete}>
              ✓ Valider le rognage
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setShowCrop(false)}>
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebcamCapture;


